import React, { useState, useEffect } from 'react';
import { X, Building, User, Phone, Mail, Truck, MessageSquare, MapPin, Search, Plus } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';

const THAI_PROVINCES = [
  "กรุงเทพมหานคร", "กระบี่", "กาญจนบุรี", "กาฬสินธุ์", "กำแพงเพชร", "ขอนแก่น", "จันทบุรี", "ฉะเชิงเทรา", "ชลบุรี", "ชัยนาท", "ชัยภูมิ", "ชุมพร", "เชียงราย", "เชียงใหม่", "ตรัง", "ตราด", "ตาก", "นครนายก", "นครปฐม", "นครพนม", "นครราชสีมา", "นครศรีธรรมราช", "นครสวรรค์", "นนทบุรี", "นราธิวาส", "น่าน", "บึงกาฬ", "บุรีรัมย์", "ปทุมธานี", "ประจวบคีรีขันธ์", "ปราจีนบุรี", "ปัตตานี", "พระนครศรีอยุธยา", "พะเยา", "พังงา", "พัทลุง", "พิจิตร", "พิษณุโลก", "เพชรบุรี", "เพชรบูรณ์", "แพร่", "ภูเก็ต", "มหาสารคาม", "มุกดาหาร", "แม่ฮ่องสอน", "ยโสธร", "ยะลา", "ร้อยเอ็ด", "ระนอง", "ระยอง", "ราชบุรี", "ลพบุรี", "ลำปาง", "ลำพูน", "เลย", "ศรีสะเกษ", "สกลนคร", "สงขลา", "สตูล", "สมุทรปราการ", "สมุทรสงคราม", "สมุทรสาคร", "สระแก้ว", "สระบุรี", "สิงห์บุรี", "สุโขทัย", "สุพรรณบุรี", "สุราษฎร์ธานี", "สุรินทร์", "หนองคาย", "หนองบัวลำภู", "อ่างทอง", "อำนาจเจริญ", "อุดรธานี", "อุตรดิตถ์", "อุทัยธานี", "อุบลราชธานี"
];

export default function NewLeadModal({ isOpen, onClose, locationData, user }) {
  const { activeLogo, hasCustomLogo, companyName, companySubtitle } = useCompany();
  const [formData, setFormData] = useState({
    company_name_th: '',
    company_name_en: '',
    customer_type: 'Existing Customer',
    channel: 'Line OA',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    requirements: [{ type: '', model: '' }],
    condition: 'New',
    required_option: 'Buy',
    requirement_detail: '',
    key_account_check: '',
    province: '',
    address: '',
    region: 'Central',
    assign_to_sales_team: '',
      cc_emails: ''
  });
  const [teams, setTeams] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
    const [ccInputValue, setCcInputValue] = useState('');
    const [isEmailEdited, setIsEmailEdited] = useState(false);
    const [customEmailBody, setCustomEmailBody] = useState('');
    const [isSubjectEdited, setIsSubjectEdited] = useState(false);
    const [customSubject, setCustomSubject] = useState('');

  useEffect(() => {
    if (isOpen) {
      const name = locationData?.name || '';
      const hasThai = /[\u0E00-\u0E7F]/.test(name);
      
      setFormData({
        company_name_th: hasThai ? name : '',
        company_name_en: hasThai ? '' : name,
        customer_type: 'Existing Customer',
        channel: 'Line OA',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        requirements: [{ type: '', model: '' }],
        condition: 'New',
        required_option: 'Buy',
        requirement_detail: '',
        key_account_check: '',
        province: '',
        address: locationData?.address || '',
        region: 'ภาคกลาง',
        assign_to_sales_team: '',
        cc_emails: ''
      });
      setIsEmailEdited(false);
      setCustomEmailBody('');
      setIsSubjectEdited(false);
      setCustomSubject('');
      fetchTeams();
    }
  }, [isOpen, locationData]);

  const fetchTeams = async () => {
    try {
      const res = await fetch('/api/teams');
      const data = await res.json();
      if (data.status === 'success') {
        setTeams(data.data);
        const marketingTeam = data.data.find(t => t.team_name.toLowerCase() === 'marketing');
        if (marketingTeam && marketingTeam.team_email) {
          setFormData(prev => {
            if (!prev.cc_emails) {
              return { ...prev, cc_emails: marketingTeam.team_email };
            }
            return prev;
          });
        }
      }
    } catch (err) {
      console.error("Error fetching teams", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      ...formData,
      lat: locationData?.lat ? String(locationData.lat) : '',
      lng: locationData?.lng ? String(locationData.lng) : '',
      created_by: user?.google_email || '',
      custom_email_subject: isSubjectEdited ? customSubject : ("Please contact the customer (Marketing Lead) : " + (formData.company_name_th || '')),
      custom_email_body: document.getElementById('email-preview-content') ? document.getElementById('email-preview-content').innerHTML : getEmailTemplate()
    };

    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.status === 'success') {
        alert('Lead saved successfully and sent to Google Sheets!');
        onClose();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (err) {
      alert('Error saving lead: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // --- STYLES ---
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(4px)',
    padding: '1rem'
  };

  const modalStyle = {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '800px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    overflow: 'hidden'
  };

  const headerStyle = {
    backgroundColor: 'var(--color-primary)', // Toyota Red
    color: '#ffffff',
    padding: '1.25rem 1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.1)'
  };

  const titleStyle = {
    fontSize: '1.25rem',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    margin: 0
  };

  const bodyStyle = {
    padding: '1.5rem',
    overflowY: 'auto',
    backgroundColor: '#f8fafc'
  };

  const sectionStyle = {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1.5rem',
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
  };

  const sectionTitleStyle = {
    fontSize: '0.85rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#475569',
    marginBottom: '1.25rem',
    paddingBottom: '0.5rem',
    borderBottom: '2px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '1.25rem'
  };

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    marginBottom: '1rem'
  };

  const labelStyle = {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#334155'
  };

  const inputWrapperStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  };

  const iconStyle = {
    position: 'absolute',
    left: '10px',
    color: '#94a3b8'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.6rem 0.75rem 0.6rem 2.25rem',
    borderRadius: '6px',
    border: '1px solid #cbd5e1',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'all 0.2s',
    backgroundColor: '#ffffff',
    color: '#0f172a'
  };

  const footerStyle = {
    padding: '1.25rem 1.5rem',
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem'
  };

  const btnSecondaryStyle = {
    padding: '0.6rem 1.25rem',
    borderRadius: '6px',
    border: '1px solid #cbd5e1',
    backgroundColor: '#ffffff',
    color: '#475569',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s'
  };

  const btnPrimaryStyle = {
    padding: '0.6rem 1.5rem',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: 'var(--color-primary)',
    color: '#ffffff',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s',
    boxShadow: '0 4px 6px -1px rgba(235, 10, 30, 0.2)'
  };

  const getEmailTemplate = () => `
  <div style="font-family: Arial, sans-serif; max-width: 650px; margin: 0 auto; background-color: white; border: 1px solid #e2e8f0;">
    <!-- Header -->
    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-bottom: 2px solid #eb0a1e;">
      <tr>
        <td style="padding: 20px 30px; vertical-align: middle;">
          ${hasCustomLogo && activeLogo ? `
            <img src="${activeLogo}" alt="${companyName || 'Company Logo'}" style="max-height: 56px; max-width: 250px; object-fit: contain; display: block;" />
          ` : `
            <h1 style="color: #eb0a1e; font-size: 32px; font-weight: 900; margin: 0 0 8px 0; letter-spacing: 1px;">TOYOTA</h1>
            <div style="border-top: 2px solid #94a3b8; width: 80%; margin-bottom: 8px;"></div>
            <div style="font-size: 11px; font-weight: bold; color: #334155; line-height: 1.4;">
              ${companyName || 'TOYOTA MATERIAL HANDLING'}<br/>
              ${companySubtitle || 'WAREHOUSE SOLUTIONS THAILAND'}
            </div>
          `}
        </td>
        <td style="padding: 20px 30px; vertical-align: middle; text-align: right;">
          <div>
            <div style="font-size: 12px; color: #64748b; font-weight: bold; letter-spacing: 1px;">MARKETING</div>
            <div style="font-size: 11px; color: #334155; font-weight: bold; letter-spacing: 1px;">LEAD NOTIFICATION</div>
          </div>
        </td>
      </tr>
    </table>

    <!-- Red Title Bar -->
    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #c00000;">
      <tr>
        <td style="padding: 20px 30px; vertical-align: middle;">
          <div style="color: rgba(255,255,255,0.7); font-size: 10px; font-weight: bold; letter-spacing: 1px; margin-bottom: 5px; text-transform: uppercase;">ASSIGNMENT STATUS</div>
          <div style="color: white; font-size: 18px; font-weight: bold;">NEW MARKETING LEAD ASSIGNED</div>
        </td>
        <td style="padding: 20px 30px; vertical-align: middle; text-align: right;">
          <span style="border: 1px solid rgba(255,255,255,0.3); color: white; padding: 6px 12px; font-size: 10px; font-weight: bold; border-radius: 4px; display: inline-block;">ACTION REQUIRED</span>
        </td>
      </tr>
    </table>

    <!-- Content -->
    <table width="100%" cellpadding="0" cellspacing="0" border="0">
      <tr>
        <td style="padding: 30px;">
          <div style="font-size: 18px; font-weight: bold; color: #0f172a; margin-bottom: 20px;">
            Dear ${formData.assign_to_sales_team ? formData.assign_to_sales_team + ' Team' : 'Sales Team'},
          </div>
          
          <div style="font-size: 14px; color: #334155; line-height: 1.6; margin-bottom: 30px;">
            We would like to share a new lead received from a prospective customer. Please find the lead details below for your review.<br/><br/>
            Kindly reach out to the customer at your convenience to understand their requirements and explore a suitable solution.<br/><br/>
            Thank you for your kind support and cooperation.
          </div>

          <!-- Lead Info -->
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border: 1px solid #e2e8f0; border-radius: 4px; background-color: #f8fafc; margin-bottom: 30px;">
            <tr>
              <td style="padding: 20px;">
                <h3 style="margin: 0 0 15px 0; font-size: 14px; color: #0f172a; border-bottom: 1px solid #cbd5e1; padding-bottom: 10px; text-transform: uppercase;">Lead Details</h3>
                
                <div style="margin-bottom: 15px;">
                  <span style="font-size: 12px; color: #64748b; font-weight: bold; text-transform: uppercase; display: block; margin-bottom: 4px;">Company Name</span>
                  <span style="font-size: 15px; color: #0f172a; font-weight: 500;">${formData.company_name_th || '-'}</span>
                </div>

                <div style="margin-bottom: 15px;">
                  <span style="font-size: 12px; color: #64748b; font-weight: bold; text-transform: uppercase; display: block; margin-bottom: 4px;">Contact Person</span>
                  <span style="font-size: 14px; color: #0f172a; display: block; line-height: 1.5;">
                    ${[
                      formData.contact_name,
                      formData.contact_phone ? 'Tel: ' + formData.contact_phone : null,
                      formData.contact_email ? 'Email: ' + formData.contact_email : null
                    ].filter(Boolean).join('<br/>') || '-'}
                  </span>
                </div>

                <div style="margin-bottom: 15px;">
                  <span style="font-size: 12px; color: #64748b; font-weight: bold; text-transform: uppercase; display: block; margin-bottom: 4px;">Requirement</span>
                  <span style="font-size: 14px; color: #0f172a; display: block; line-height: 1.5;">
                    ${
                      formData.requirements.map(req => {
                        const parts = [];
                        if (req.type) parts.push('<b>' + req.type + '</b>');
                        if (req.model) parts.push('Model: ' + req.model);
                        return parts.join(' ');
                      }).filter(Boolean).join('<br/>') || '-'
                    }
                    <br/><br/>
                    ${[
                      formData.condition ? 'Condition: ' + formData.condition : null,
                      formData.required_option ? 'Option: ' + formData.required_option : null
                    ].filter(Boolean).join('<br/>')}
                  </span>
                </div>

                <div>
                  <span style="font-size: 12px; color: #64748b; font-weight: bold; text-transform: uppercase; display: block; margin-bottom: 4px;">Location</span>
                  <span style="font-size: 14px; color: #0f172a; line-height: 1.5;">
                    ${formData.address || '-'}<br/>
                    ${formData.province || '-'} (${formData.region || '-'})
                  </span>
                </div>
              </td>
            </tr>
          </table>

          <!-- Button -->
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin: 30px 0;">
            <tr>
              <td align="center">
                <a href="https://salesvigator.tmh-wst.com" style="background-color: #c00000; color: white; padding: 14px 30px; text-decoration: none; font-weight: bold; display: inline-block; font-size: 14px; letter-spacing: 0.5px;">VIEW IN SALESVIGATOR &rarr;</a>
                <div style="font-size: 11px; color: #94a3b8; margin-top: 15px;">Login to the platform for more details and history</div>
              </td>
            </tr>
          </table>

          <div style="border-top: 1px solid #e2e8f0; margin-top: 40px; padding-top: 30px; font-size: 13px; color: #334155; line-height: 1.6;">
            Yours sincerely,<br/>
            <b>${companyName || 'Toyota Material Handling Warehouse Solutions Thailand'}</b><br/>
            <span style="color: #64748b;">Marketing Lead Management Team</span>
          </div>
        </td>
      </tr>
    </table>

    <!-- Footer -->
    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #1e1e1e; border-bottom: 6px solid #c00000;">
      <tr>
        <td style="padding: 25px 30px; font-size: 11px; color: #94a3b8; line-height: 1.6; vertical-align: middle;">
          This is an automated report from the Salesvigator Marketing Lead System.<br/>
          Please do not reply directly to this email.<br/><br/>
          &copy; ${companyName || 'Toyota Material Handling Warehouse Solutions Thailand Co., Ltd.'}
        </td>
        <td style="padding: 25px 30px; vertical-align: middle; text-align: right;">
          ${hasCustomLogo && activeLogo ? `
            <div style="display: inline-block; background-color: #ffffff; padding: 6px 12px; border-radius: 4px;">
              <img src="${activeLogo}" alt="Logo" style="max-height: 26px; max-width: 120px; object-fit: contain; display: block;" />
            </div>
          ` : `
            <h2 style="color: #eb0a1e; font-size: 18px; font-weight: 900; margin: 0; letter-spacing: 1px;">TOYOTA</h2>
          `}
        </td>
      </tr>
    </table>
  </div>
`;

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            <Building size={24} />
            Create New Marketing Lead
          </h2>
          <button 
            onClick={onClose} 
            style={{ background: 'transparent', border: 'none', color: '#ffffff', cursor: 'pointer', padding: '0.25rem' }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          <form id="new-lead-form" onSubmit={handleSubmit}>
            
            {/* Customer Info Section */}
            <div style={sectionStyle}>
              <h3 style={sectionTitleStyle}>
                <User size={16} /> Customer Information
              </h3>
              <div style={gridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>ชื่อบริษัท (TH) {!formData.company_name_en && <span style={{ color: '#ef4444' }}>*</span>}</label>
                  <div style={inputWrapperStyle}>
                    <Building size={16} style={iconStyle} />
                    <input required={!formData.company_name_en} type="text" name="company_name_th" value={formData.company_name_th} onChange={handleChange} style={inputStyle} placeholder="ชื่อบริษัท (ภาษาไทย)..." />
                  </div>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Company Name (EN) <span style={{ color: '#ef4444' }}>*</span></label>
                  <div style={inputWrapperStyle}>
                    <Building size={16} style={iconStyle} />
                    <input required type="text" name="company_name_en" value={formData.company_name_en} onChange={handleChange} style={inputStyle} placeholder="Company Name (English)..." />
                  </div>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Customer Type <span style={{ color: '#ef4444' }}>*</span></label>
                  <select required name="customer_type" value={formData.customer_type} onChange={handleChange} style={{...inputStyle, paddingLeft: '0.75rem'}}>
                    <option value="Existing Customer">Existing Customer</option>
                    <option value="New Customer">New Customer</option>
                  </select>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Channel <span style={{ color: '#ef4444' }}>*</span></label>
                  <select required name="channel" value={formData.channel} onChange={handleChange} style={{...inputStyle, paddingLeft: '0.75rem'}}>
                    <option value="Line OA">Line OA</option>
                    <option value="Marketing Visit">Marketing Visit</option>
                    <option value="Email">Email</option>
                    <option value="Website (Form)">Website (Form)</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Connection">Connection</option>
                    <option value="Office Phone">Office Phone</option>
                    <option value="Event">Event</option>
                    <option value="TMHTH">TMHTH</option>
                  </select>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Contact Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <div style={inputWrapperStyle}>
                    <User size={16} style={iconStyle} />
                    <input required type="text" name="contact_name" value={formData.contact_name} onChange={handleChange} style={inputStyle} placeholder="John Doe" />
                  </div>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
                  <div style={inputWrapperStyle}>
                    <Phone size={16} style={iconStyle} />
                    <input required type="tel" name="contact_phone" value={formData.contact_phone} onChange={handleChange} style={inputStyle} placeholder="081-xxx-xxxx" />
                  </div>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Email <span style={{ color: '#ef4444' }}>*</span></label>
                  <div style={inputWrapperStyle}>
                    <Mail size={16} style={iconStyle} />
                    <input required type="email" name="contact_email" value={formData.contact_email} onChange={handleChange} style={inputStyle} placeholder="john@example.com" />
                  </div>
                </div>
              </div>
            </div>

            {/* Requirement Section */}
            <div style={sectionStyle}>
              <h3 style={sectionTitleStyle}>
                <Truck size={16} /> Requirement Details
              </h3>
              <div style={gridStyle}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Requirements (Type & Model) <span style={{ color: '#ef4444' }}>*</span></label>
                  {formData.requirements.map((req, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <select 
                          required 
                          value={req.type}
                          onChange={(e) => {
                            const newReqs = [...formData.requirements];
                            newReqs[idx].type = e.target.value;
                            setFormData({ ...formData, requirements: newReqs });
                          }}
                          style={{...inputStyle, paddingLeft: '0.75rem', appearance: 'auto'}}
                        >
                          <option value="">-- Add Type --</option>
                          <option value="Allied Product">Allied Product</option>
                          <option value="Automation">Automation</option>
                          <option value="Battery">Battery</option>
                          <option value="CB">CB</option>
                          <option value="Charger">Charger</option>
                          <option value="Double Deep Reach Truck">Double Deep Reach Truck</option>
                          <option value="Electric CB">Electric CB</option>
                          <option value="Engine CB">Engine CB</option>
                          <option value="Hand Pallet">Hand Pallet</option>
                          <option value="Inquiry">Inquiry</option>
                          <option value="Order Picker">Order Picker</option>
                          <option value="Parts">Parts</option>
                          <option value="Powered Pallet">Powered Pallet</option>
                          <option value="Rack">Rack</option>
                          <option value="Reach Truck">Reach Truck</option>
                          <option value="Service">Service</option>
                          <option value="Stacker">Stacker</option>
                          <option value="Tow Tractor">Tow Tractor</option>
                          <option value="VNA">VNA</option>
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <input 
                          type="text" 
                          placeholder="Required Model (e.g. 8FBN25)" 
                          value={req.model}
                          onChange={(e) => {
                            const newReqs = [...formData.requirements];
                            newReqs[idx].model = e.target.value;
                            setFormData({ ...formData, requirements: newReqs });
                          }}
                          style={{...inputStyle, paddingLeft: '0.75rem'}} 
                          required
                        />
                      </div>
                      {formData.requirements.length > 1 && (
                        <button 
                          type="button" 
                          onClick={() => {
                            const newReqs = formData.requirements.filter((_, i) => i !== idx);
                            setFormData({ ...formData, requirements: newReqs });
                          }}
                          style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', padding: '8px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button 
                    type="button" 
                    onClick={() => setFormData({ ...formData, requirements: [...formData.requirements, { type: '', model: '' }] })}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#0369a1', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', padding: '6px 12px', borderRadius: '6px', marginTop: '4px' }}
                  >
                    <Plus size={14} /> Add Requirement
                  </button>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Requirement (New/Used) <span style={{ color: '#ef4444' }}>*</span></label>
                  <select required name="condition" value={formData.condition} onChange={handleChange} style={{...inputStyle, paddingLeft: '0.75rem'}}>
                    <option value="New">New</option>
                    <option value="Used">Used</option>
                    <option value="Both">Both</option>
                  </select>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Required Option <span style={{ color: '#ef4444' }}>*</span></label>
                  <select required name="required_option" value={formData.required_option} onChange={handleChange} style={{...inputStyle, paddingLeft: '0.75rem'}}>
                    <option value="Buy">Buy</option>
                    <option value="Rent">Rent</option>
                    <option value="Both">Both</option>
                  </select>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Key Account Check <span style={{ color: '#ef4444' }}>*</span></label>
                  <input required type="text" name="key_account_check" value={formData.key_account_check} onChange={handleChange} style={{...inputStyle, paddingLeft: '0.75rem'}} placeholder="Check details..." />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Requirement Detail <span style={{ color: '#ef4444' }}>*</span></label>
                  <div style={{...inputWrapperStyle, alignItems: 'flex-start'}}>
                    <MessageSquare size={16} style={{...iconStyle, top: '10px'}} />
                    <textarea required name="requirement_detail" value={formData.requirement_detail} onChange={handleChange} rows="2" style={{...inputStyle, resize: 'vertical'}} placeholder="Customer is looking for..."></textarea>
                  </div>
                </div>
              </div>
            </div>

            {/* Location & Assignment */}
            <div style={sectionStyle}>
              <h3 style={sectionTitleStyle}>
                <MapPin size={16} /> Location & Assignment
              </h3>
              <div style={gridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Address <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 'normal' }}>(ระบุที่อยู่ หรือปักหมุดบนแผนที่ภายหลังได้)</span></label>
                  <div style={{...inputWrapperStyle, alignItems: 'flex-start'}}>
                    <MapPin size={16} style={{...iconStyle, top: '10px'}} />
                    <textarea name="address" value={formData.address} onChange={handleChange} rows="3" style={{...inputStyle, resize: 'vertical'}} placeholder="ระบุที่อยู่ / สถานที่ (หากยังไม่ทราบ สามารถปักหมุดภายหลังได้)..."></textarea>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>Province</label>
                      <input list="province-list" type="text" name="province" value={formData.province} onChange={handleChange} style={{...inputStyle, paddingLeft: '0.75rem'}} placeholder="เลือกหรือพิมพ์จังหวัด..." />
                      <datalist id="province-list">
                        {THAI_PROVINCES.map(p => (
                          <option key={p} value={p} />
                        ))}
                      </datalist>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>Region</label>
                      <select name="region" value={formData.region} onChange={handleChange} style={{...inputStyle, paddingLeft: '0.75rem'}}>
                        <option value="">-- เลือกภาค --</option>
                        <option value="ภาคกลาง">ภาคกลาง</option>
                        <option value="ภาคตะวันตก">ภาคตะวันตก</option>
                        <option value="ภาคตะวันออก">ภาคตะวันออก</option>
                        <option value="ภาคตะวันออกเฉียงเหนือ">ภาคตะวันออกเฉียงเหนือ</option>
                        <option value="ภาคใต้">ภาคใต้</option>
                        <option value="ภาคเหนือ">ภาคเหนือ</option>
                      </select>
                    </div>
                  </div>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>Assign to Sales Team *</label>
                    <div style={inputWrapperStyle}>
                      <Search size={16} style={iconStyle} />
                      <select required name="assign_to_sales_team" value={formData.assign_to_sales_team} onChange={handleChange} style={inputStyle}>
                        <option value="">-- Select Team --</option>
                        {teams.map(team => (
                          <option key={team.id} value={team.team_name}>{team.team_name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

                        {/* Email Notification Preview */}
              <div style={sectionStyle}>
                <h3 style={sectionTitleStyle}>
                  <Mail size={16} /> Email Notification
                </h3>
                
                {formData.assign_to_sales_team && teams.find(t => t.team_name === formData.assign_to_sales_team) && (
                  <div style={{...formGroupStyle, marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem'}}>
                    <div style={{fontWeight: '500', color: '#166534', fontSize: '0.9rem'}}>
                      ส่ง To ทีม: {teams.find(t => t.team_name === formData.assign_to_sales_team)?.team_name} 
                      <span style={{color: '#15803d', marginLeft: '0.5rem', fontWeight: 'normal'}}>&lt;{teams.find(t => t.team_name === formData.assign_to_sales_team)?.team_email}&gt;</span>
                    </div>
                  </div>
                )}

                <div style={formGroupStyle}>
                  <label style={labelStyle}>CC Email(s) <span style={{fontSize: '0.75rem', fontWeight: 'normal', color: '#94a3b8'}}>(Press Enter or select team)</span></label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {formData.cc_emails && formData.cc_emails.split(',').map(e => e.trim()).filter(e => e).map((email, i) => (
                        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', backgroundColor: '#e2e8f0', color: '#334155', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.85rem' }}>
                          {email}
                          <X size={14} style={{ cursor: 'pointer', color: '#64748b' }} onClick={() => {
                              const emails = (formData.cc_emails || '').split(',').map(e => e.trim()).filter(e => e && e !== email);
                              setFormData({...formData, cc_emails: emails.join(', ')});
                          }} />
                        </span>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input type="text" value={ccInputValue} onChange={(e) => setCcInputValue(e.target.value)} onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                              e.preventDefault();
                              const val = ccInputValue.trim();
                              if (val && formData.cc_emails) {
                                  setFormData({...formData, cc_emails: formData.cc_emails + ', ' + val});
                              } else if (val) {
                                  setFormData({...formData, cc_emails: val});
                              }
                              setCcInputValue('');
                          }
                      }} style={{...inputStyle, paddingLeft: '0.75rem', flex: 1}} placeholder="Type email and press Enter..." />
                      <select onChange={(e) => {
                          if (e.target.value) {
                              const val = e.target.value;
                              if (val && formData.cc_emails) {
                                  setFormData({...formData, cc_emails: formData.cc_emails + ', ' + val});
                              } else if (val) {
                                  setFormData({...formData, cc_emails: val});
                              }
                              e.target.value = '';
                          }
                      }} style={{...inputStyle, paddingLeft: '0.75rem', width: 'auto', backgroundColor: '#f8fafc', cursor: 'pointer', border: '1px solid #cbd5e1'}}>
                        <option value="">+ Add Team CC</option>
                        {teams.filter(t => t.team_email).map(team => (
                          <option key={'cc-'+team.id} value={team.team_email}>{team.team_name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div style={{...formGroupStyle, marginTop: '1rem'}}>
                  <label style={labelStyle}>หัวข้ออีเมล (Subject)</label>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input 
                      type="text" 
                      value={isSubjectEdited ? customSubject : ("Please contact the customer (Marketing Lead) : " + (formData.company_name_th || ''))}
                      onChange={(e) => {
                        setIsSubjectEdited(true);
                        setCustomSubject(e.target.value);
                      }}
                      style={inputStyle} 
                    />
                    {isSubjectEdited && (
                      <button type="button" onClick={() => setIsSubjectEdited(false)} style={{ marginLeft: '8px', background: '#e2e8f0', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', color: '#334155', fontWeight: 'bold', fontSize: '12px', flexShrink: 0 }}>
                        ↺ รีเฟรสข้อมูล
                      </button>
                    )}
                  </div>
                </div>

                <div style={{...formGroupStyle, marginTop: '1rem'}}>
                    <label style={labelStyle}>ตัวอย่างเนื้อหาอีเมลที่จะส่ง</label>
                    <div style={{ padding: '8px 16px', backgroundColor: '#f1f5f9', fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic', borderBottom: '1px solid #e2e8f0', borderTopLeftRadius: '6px', borderTopRightRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>✨ คุณสามารถคลิกเพื่อแก้ไข เพิ่ม หรือลบข้อมูลในเนื้อหาด้านล่างนี้ได้ก่อนกด Submit</span>
                      {isEmailEdited && (
                        <button 
                          type="button" 
                          onClick={() => {
                            setIsEmailEdited(false);
                            setCustomEmailBody('');
                            const el = document.getElementById('email-preview-content');
                            if (el) el.innerHTML = getEmailTemplate();
                          }} 
                          style={{ background: '#e2e8f0', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', color: '#334155', fontWeight: 'bold' }}
                        >
                          ↺ รีเฟรสข้อมูล
                        </button>
                      )}
                    </div>
                    <div 
                      id="email-preview-content"
                      style={{ padding: '12px 16px', backgroundColor: 'white', lineHeight: 1.7, outline: 'none', minHeight: '150px', border: '1px solid #e2e8f0', borderTop: 'none', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px' }}
                      contentEditable={true}
                      suppressContentEditableWarning={true}
                      onBlur={(e) => {
                        setIsEmailEdited(true);
                        setCustomEmailBody(e.currentTarget.innerHTML);
                      }}
                      dangerouslySetInnerHTML={{ __html: isEmailEdited ? customEmailBody : getEmailTemplate() }}
                    />
                </div>
              </div>

          </form>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button 
            type="button" 
            onClick={onClose} 
            disabled={isSubmitting}
            style={btnSecondaryStyle}
            onMouseOver={e => e.target.style.backgroundColor = '#f1f5f9'}
            onMouseOut={e => e.target.style.backgroundColor = '#ffffff'}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            form="new-lead-form"
            disabled={isSubmitting}
            style={{...btnPrimaryStyle, opacity: isSubmitting ? 0.7 : 1}}
            onMouseOver={e => { if(!isSubmitting) e.target.style.backgroundColor = 'var(--color-primary-hover)'}}
            onMouseOut={e => { if(!isSubmitting) e.target.style.backgroundColor = 'var(--color-primary)'}}
          >
            {isSubmitting ? 'Saving...' : 'Submit Lead'}
          </button>
        </div>

      </div>
    </div>
  );
}
